import * as H from 'history';
import * as React from 'react';
import {Prompt, RouteComponentProps} from 'react-router';
import {withRouter} from 'react-router-dom';
import cx from 'classnames';
import root from 'window-or-global';
import uuid from 'uuid/v1';

import RedirectWithParams from '../redirect/RedirectWithParams';
import {DefaultConfirmationAlert, PromptRenderProps} from './AlertPrompts';
import styles from './AlertPrompts.pcss';

/*
    Based on Kamranicus' article
    https://kamranicus.com/posts/2018-07-26-react-router-custom-transition-ui
    https://codesandbox.io/s/400wzxnwr0
*/
interface Props extends RouteComponentProps {
    onConfirm: () => void;
    render: (props: PromptRenderProps) => React.ReactElement<any>;
    transitionOnCancel: boolean;
    when: boolean;
}

class PreventTransitionPrompt extends React.Component<Props> {
    static defaultProps = {
        render: DefaultConfirmationAlert,
        transitionOnCancel: true
    };

    _isMounted = false;
    __trigger: symbol;

    state = {
        open: false,
        confirmed: false,
        allowedToTransition: false,
        next: false
    };

    constructor(props) {
        super(props);

        // Define a unique global symbol to store
        // a dialog trigger reference accessible via
        // a string key and unique id
        this.__trigger = Symbol.for(`__PreventTransitionPrompt__${uuid()}`);
    }

    /**
     * Attach global dialog trigger for this component
     * instance to our Symbol trigger.
     * Requires React Router with getUserConfirmation override.
     * see diesel-site/app/scripts/client/configureStore.js
     */
    componentDidMount() {
        this._isMounted = true;
        root[this.__trigger] = this.show;
    }

    /**
     * Ensure we clean up and remove the reference
     * from the global object
     */
    componentWillUnmount() {
        this._isMounted = false;
        delete root[this.__trigger];
    }

    render() {
        const {when, render} = this.props;
        const {open, next, allowedToTransition} = this.state;
        const modalStyles = cx([
            styles.window,
            {
                [styles.showWindow]: open
            }
        ]);

        if (allowedToTransition && next) {
            return <RedirectWithParams to={next} />;
        }

        const renderProps = {
            onClose: this.handleClose,
            onCancel: this.handleClose,
            onConfirm: this.handleConfirm,
            ...this.props
        };

        return (
            <div>
                {/* React Router prompt, callback will return true to allow transition or dialog key to prevent */}
                <Prompt when={when} message={this.handleTransition} />

                <div className={modalStyles}>
                    <div className={styles.content}>{render(renderProps)}</div>
                </div>
            </div>
        );
    }

    /**
     * Show the dialog. Invoked primarily from React Router transition
     * handler getUserConfirmation.
     *
     * @param allowTransitionCallback A function that accepts a flag whether or not to allow the route transition
     */
    show = (allowTransitionCallback: (allowRouteTransition: boolean) => void) => {
        // we are immediately preventing any transitions here
        // but could just as easily base this off a user interaction
        // or other state
        this._isMounted && this.setState({open: true}, () => allowTransitionCallback(false));
    };

    /**
     * Closes the dialog
     */
    handleClose = () => {
        this._isMounted &&
            this.setState({
                open: false,
                allowedToTransition: this.props.transitionOnCancel
            });
    };

    handleConfirm = () => {
        if (this.props.onConfirm) {
            this.props.onConfirm();
        }
        this._isMounted && this.setState({open: false, confirmed: true, allowedToTransition: true});
    };

    /**
     * Handles the Router transition. Returns true if allowed
     * or the dialog trigger key to enable the dialog.
     */

    handleTransition = (location: H.Location): string | boolean => {
        this._isMounted &&
            this.setState({
                next: location.pathname
            });

        const {when} = this.props;
        const key = Symbol.keyFor(this.__trigger);

        return when && key ? key : true;
    };
}

export default withRouter(PreventTransitionPrompt);
